/**
 * MID Health (Step 5, read-only v1).
 * Surfaces merchants that reached approval but are not actually processing yet.
 * Volume anomaly / processing-stop detection is deliberately out of scope here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, CheckCircle2, Clock, Hourglass, XCircle } from 'lucide-react';
import api from '../lib/api';

const HEALTH_STYLES = {
  'Active — Processing': { icon: CheckCircle2, tone: 'text-emerald-600 bg-emerald-500/10' },
  'Active — No Processing Yet': { icon: AlertTriangle, tone: 'text-amber-600 bg-amber-500/10' },
  'Approved — Awaiting Activation': { icon: Clock, tone: 'text-sky-600 bg-sky-500/10' },
};

const PREFIX_STYLES = [
  ['Suspended', { icon: AlertTriangle, tone: 'text-rose-600 bg-rose-500/10' }],
  ['Closed', { icon: XCircle, tone: 'text-muted-foreground bg-muted' }],
  ['Submitted', { icon: Hourglass, tone: 'text-violet-600 bg-violet-500/10' }],
  ['Pending', { icon: Hourglass, tone: 'text-violet-600 bg-violet-500/10' }],
];

const fallbackStyle = { icon: Clock, tone: 'text-muted-foreground bg-muted' };

const styleFor = (health) =>
  HEALTH_STYLES[health] ||
  (PREFIX_STYLES.find(([p]) => health.startsWith(p)) || [null, fallbackStyle])[1];

export default function MidHealthPage() {
  const [data, setData] = useState({ counts: {}, accounts: [] });
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.getMidHealth(filter));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-8" data-testid="mid-health-page">
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6 text-primary" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">MID Health</h1>
        </div>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl">
          Every boarded MID and whether it is actually processing. Anything sitting in
          “awaiting activation” is revenue that has been sold but never switched on.
        </p>
      </header>

      <div className="flex flex-wrap gap-3">
        {Object.entries(data.counts || {}).map(([health, count]) => {
          const style = styleFor(health);
          const Icon = style.icon;
          const active = filter === health;
          return (
            <button
              key={health}
              onClick={() => setFilter(active ? '' : health)}
              className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                active ? 'border-primary' : 'border-border hover:border-primary/40'
              }`}
              data-testid={`mid-health-card-${health.replace(/[^a-zA-Z]+/g, '-').toLowerCase()}`}
            >
              <span className={`rounded-lg p-2 ${style.tone}`}><Icon className="h-4 w-4" /></span>
              <span>
                <span className="block text-2xl font-semibold leading-none">{count}</span>
                <span className="block text-xs text-muted-foreground mt-1">{health}</span>
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground" data-testid="mid-health-loading">Loading…</p>
      ) : (data.accounts || []).length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center" data-testid="mid-health-empty">
          <p className="text-base font-medium">No processing accounts to show</p>
          <p className="text-sm text-muted-foreground mt-1">
            MIDs appear here as soon as an application is approved and boarded.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border" data-testid="mid-health-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left">Merchant</th>
                <th className="px-4 py-3 text-left">Location</th>
                <th className="px-4 py-3 text-left">MID</th>
                <th className="px-4 py-3 text-left">Processor</th>
                <th className="px-4 py-3 text-left">Health</th>
                <th className="px-4 py-3 text-right">Days in status</th>
              </tr>
            </thead>
            <tbody>
              {data.accounts.map((a) => {
                const style = styleFor(a.health);
                const days = a.days_since_activated ?? a.days_since_approved;
                return (
                  <tr
                    key={a.processing_account_id}
                    className="border-t border-border hover:bg-muted/30"
                    data-testid={`mid-health-row-${a.processing_account_id}`}
                  >
                    <td className="px-4 py-3 font-medium">{a.merchant_group_name || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{a.location_name || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs">{a.mid || '—'}</td>
                    <td className="px-4 py-3 capitalize text-muted-foreground">{a.processor || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${style.tone}`}>
                        {a.health}
                      </span>
                    </td>
                    <td
                      className="px-4 py-3 text-right text-muted-foreground"
                      title={a.days_since_activated != null ? 'Days since activation' : 'Days since approval'}
                    >
                      {days === null || days === undefined ? '—' : `${days}d`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
